import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import SEOData from '@/components/base/SEOData';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const comic = comics.find(c => c.id === Number(id));

  const [expandedDesc, setExpandedDesc] = useState(false);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-book-2-line text-foreground-400 text-4xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">漫画未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">抱歉，该漫画不存在或已下架</p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find(c => c.slug === comic.category);
  const formatViews = (views: number) => views >= 10000 ? (views / 10000).toFixed(1) + '万' : views.toString();
  const sortedChapters = [...comic.chapters].sort((a, b) =>
    sortOrder === 'asc' ? a.num - b.num : b.num - a.num
  );
  const descIsLong = comic.description.length > 80;

  return (
    <>
      <SEOMeta
        title={`${comic.title} - ${comic.author} - 免费韩漫在线阅读 - 韩漫漫画网`}
        description={`${comic.title}：${comic.description}。免费韩国漫画下拉式在线阅读，高清画质，韩漫漫画网。`}
        keywords={`${comic.title},${comic.author},免费韩漫,韩国漫画,高清漫画,韩漫漫画网,${category?.name || ''}`}
        canonicalUrl={`http://aetws.cn/comic/${comic.id}`}
        ogType="article"
      />
      <SEOData type="Article" data={{
        headline: `${comic.title} - ${comic.author}`,
        description: comic.description,
        author: { '@type': 'Person', name: comic.author },
        datePublished: comic.chapters[0].updateDate,
        dateModified: comic.lastUpdated,
        inLanguage: 'zh-CN',
        isPartOf: { '@type': 'WebSite', name: '韩漫漫画网', url: 'http://aetws.cn/' },
      }} />
      <SEOData type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'http://aetws.cn/' },
          { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `http://aetws.cn/category/${comic.category}` },
          { '@type': 'ListItem', position: 3, name: comic.title, item: `http://aetws.cn/comic/${comic.id}` },
        ],
      }} />
      <Navbar />
      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: category?.name || '分类', to: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        {/* Comic Info */}
        <section className="w-full py-8 md:py-10 bg-background-50">
          <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-[280px] shrink-0">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - ${comic.author} - 免费韩漫`}
                    title={`${comic.title} - ${comic.author}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-accent-500/10 text-accent-500'
                      : 'bg-secondary-500/10 text-secondary-500'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-foreground-500 whitespace-nowrap">
                    <i className="ri-user-line text-xs"></i> {comic.author}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-foreground-500 whitespace-nowrap">
                    <i className="ri-star-fill text-xs text-accent-500"></i> {comic.rating}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-foreground-500 whitespace-nowrap">
                    <i className="ri-eye-line text-xs"></i> {formatViews(comic.views)}次阅读
                  </span>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {descIsLong && !expandedDesc
                    ? comic.description.slice(0, 80) + '...'
                    : comic.description
                  }
                  {descIsLong && (
                    <button
                      onClick={() => setExpandedDesc(!expandedDesc)}
                      className="text-accent-500 ml-1 cursor-pointer hover:underline whitespace-nowrap text-sm"
                    >
                      {expandedDesc ? '收起' : '展开'}
                    </button>
                  )}
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => {
                      const firstChapter = document.getElementById('chapters');
                      if (firstChapter) firstChapter.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-line"></i> 开始阅读
                  </button>
                  <button
                    onClick={() => {
                      const chapterList = document.getElementById('chapters');
                      if (chapterList) chapterList.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="inline-flex items-center gap-2 bg-background-200 text-foreground-700 px-6 py-3 rounded-full text-sm font-medium hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-bookmark-line"></i> 查看目录
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section id="chapters" className="w-full py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                章节目录 <span className="text-foreground-400 text-base font-normal">（共 {comic.chapters.length} 话）</span>
              </h2>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-accent-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className={`text-sm ${sortOrder === 'asc' ? 'ri-sort-asc' : 'ri-sort-desc'}`}></i>
                {sortOrder === 'asc' ? '正序' : '倒序'}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {sortedChapters.map((chapter) => (
                <a
                  key={chapter.num}
                  href={`#chapter-${chapter.num}`}
                  className="flex items-center justify-between bg-background-50 rounded-xl px-5 py-4 hover:bg-accent-500 hover:text-background-50 transition-all duration-300 group cursor-pointer"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-sm font-medium whitespace-nowrap group-hover:text-background-50">
                      第{chapter.num}话
                    </span>
                    <span className="text-sm text-foreground-600 group-hover:text-background-50/80 truncate">
                      {chapter.title}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-400 group-hover:text-background-50/60 whitespace-nowrap ml-3 shrink-0">
                    {chapter.pageCount}页
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Related Comics */}
        <section className="w-full py-14 md:py-16 bg-background-50">
          <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {comics
                .filter(c => c.id !== comic.id && (c.category === comic.category || c.status === comic.status))
                .slice(0, 6)
                .map((related) => (
                  <Link
                    key={related.id}
                    to={`/comic/${related.id}`}
                    className="group block rounded-2xl bg-background-100 overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                    data-product-shop=""
                  >
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={related.cover}
                        alt={`${related.title} - ${related.author}`}
                        title={`${related.title} - ${related.author}`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-foreground-950 truncate">{related.title}</h3>
                      <p className="text-xs text-foreground-500 mt-1">{related.author}</p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}